import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import type { PlatformRole } from '@barbeiropro/contracts';
import { EmptyState, LoadingState } from '@/components/shared/states';
import { api, HttpError } from '@/lib/api';
import { useMe } from '@/lib/session';

export const platformMeKey = ['platform', 'me'] as const;

/** Papel do usuário no admin da plataforma (null = não é administrador). */
export function usePlatformMe() {
  return useQuery({
    queryKey: platformMeKey,
    queryFn: async () => {
      try {
        return (await api.get<{ role: PlatformRole }>('/platform/me')).role;
      } catch (e) {
        if (e instanceof HttpError && (e.status === 403 || e.status === 401)) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });
}

/** Esconde a tela de quem não é administrador da plataforma. A checagem real é sempre no backend. */
export function RequirePlatformAdmin({ children }: { children: ReactNode }) {
  const me = useMe();
  const role = usePlatformMe();
  const location = useLocation();
  if (me.isLoading || role.isLoading) return <LoadingState />;
  if (!me.data) return <Navigate to={`/entrar?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (!role.data) {
    return (
      <div className="mx-auto max-w-lg p-6">
        <EmptyState title="Acesso restrito" description="Esta área é exclusiva da administração da plataforma." action={<a className="text-sm underline" href="/app">Voltar ao app</a>} />
      </div>
    );
  }
  return <>{children}</>;
}
